import React, { useState } from "react";
import { Button, Form, ListGroup, Modal, Spinner } from "react-bootstrap";
import { notifyError, notifySuccess } from "../../../components/AppToast";
import {
  useAddHeadquarterMember,
  useHeadquarters,
  useRemoveHeadquarterMember,
} from "../hooks/useHeadquarters";
import { useMemberships } from "../../billing/hooks/useMemberships";

function errorText(err, fallback) {
  const data = err?.response?.data;
  const fieldError = data?.field_errors
    ? Object.values(data.field_errors).flat()[0]
    : null;
  return fieldError ?? data?.user?.[0] ?? data?.detail ?? fallback;
}

/**
 * Acceso a una sede: su administrador (fijo) decide qué otros propietarios
 * también la ven cuando la academia no comparte entre sedes. Lee la sede
 * desde la lista ya cacheada para reflejar los cambios al instante.
 */
function HeadquartersAccessModal({ show, onHide, hqId }) {
  const { data: hqs } = useHeadquarters();
  const { data: owners } = useMemberships(show ? "OWNER" : undefined);
  const addMutation = useAddHeadquarterMember();
  const removeMutation = useRemoveHeadquarterMember();
  const [selected, setSelected] = useState("");

  const hq = (hqs ?? []).find((h) => h.id === hqId);
  if (!hq) return null;

  const members = hq.members_display ?? [];
  const taken = new Set([hq.administrator, ...members.map((m) => m.id)]);
  const candidates = (owners ?? []).filter((m) => !taken.has(m.user.id));

  const handleAdd = () => {
    if (!selected) return;
    addMutation.mutate(
      { id: hq.id, user: Number(selected) },
      {
        onSuccess: () => {
          setSelected("");
          notifySuccess("Acceso concedido");
        },
        onError: (err) => notifyError(errorText(err, "No se pudo dar acceso.")),
      }
    );
  };

  const handleRemove = (member) => {
    removeMutation.mutate(
      { id: hq.id, user: member.id },
      {
        onSuccess: () => notifySuccess(`Se quitó el acceso a ${member.name}`),
        onError: (err) => notifyError(errorText(err, "No se pudo quitar el acceso.")),
      }
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Acceso a {hq.reference_name || hq.address}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          Administrador: <strong>{hq.administrator_display}</strong>
        </p>

        <h6 className="text-uppercase text-muted small">
          Otros propietarios con acceso
        </h6>
        {members.length === 0 ? (
          <p className="text-muted">Nadie más tiene acceso a esta sede.</p>
        ) : (
          <ListGroup className="mb-3">
            {members.map((m) => (
              <ListGroup.Item
                key={m.id}
                className="d-flex justify-content-between align-items-center"
              >
                {m.name}
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => handleRemove(m)}
                  disabled={removeMutation.isPending}
                >
                  Quitar
                </Button>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}

        <Form.Group controlId="hq-access-add">
          <Form.Label>Dar acceso a otro propietario</Form.Label>
          <div className="d-flex gap-2">
            <Form.Select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              style={{ minHeight: 44 }}
            >
              <option value="">
                {candidates.length === 0
                  ? "No hay más propietarios para agregar"
                  : "Selecciona un propietario…"}
              </option>
              {candidates.map((m) => (
                <option key={m.user.id} value={m.user.id}>
                  {m.user.full_name || m.user.email}
                </option>
              ))}
            </Form.Select>
            <Button
              onClick={handleAdd}
              disabled={!selected || addMutation.isPending}
              style={{ minHeight: 44, whiteSpace: "nowrap" }}
            >
              {addMutation.isPending ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Dar acceso"
              )}
            </Button>
          </div>
          <Form.Text muted>
            Solo aplica si la academia no comparte temas económicos u
            operativos entre sedes.
          </Form.Text>
        </Form.Group>
      </Modal.Body>
    </Modal>
  );
}

export { HeadquartersAccessModal };
